import React, { memo, useCallback, useContext, useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import Chat from 'supportAssistant/components/Chat'
import History from 'supportAssistant/components/History'
import Support from 'supportAssistant/components/Support'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { getSupportChats, getSupportChatById, updateChat } from 'api/support'
import { useAppSelector } from 'hooks/reduxHooks'
import Modal from 'components/Luxkit/Modal/Modal'
import { ChatStatuses } from 'supportAssistant/constants/chatStatuses'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import { SocketContext } from '../contexts/SocketContext'
import ModalContext from 'contexts/ModalContext'

const StyledLayoutContainer = styled.div`
  display: grid;
  grid-template-areas:
    "scroll scroll scroll"
    "chat chat chat";
  padding: 0;
  min-height: ${rem(600)};
  height: calc(100vh + 96px); // measured mobile header height = 191px

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 3fr 1fr;
    grid-template-areas: "history chat support";
    height: calc(100vh - ${rem(172)}); // hand measured desktop header height = 172px
  }
`
const HistoryContainer = styled(CSSBreakpoint)`
  grid-area: history;
  // max-width: ${rem(350)};
  height: inherit;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  border-right: none;
`

const StyledBodyText = styled(BodyText)`
  margin-bottom: ${rem(16)};
`

const ChatContainer = styled.div`
  grid-area: chat;
  // max-width: ${rem(768)};
  height: inherit;
  border: 1px solid ${props => props.theme.palette.neutral.five};
`

const SupportContainer = styled(CSSBreakpoint)`
  grid-area: support;
  // max-width: ${rem(350)};
  height: inherit;
  border: 1px solid ${props => props.theme.palette.neutral.five};
  border-left: none;
`

const ScrollTo = styled.div`
  visibility: hidden;
`

function ChatPage() {
  const [chatsHistory, setChatsHistory] = useState<any[]>([])
  const [activeChatId, setActiveChatId] = useState<string | undefined>(undefined)
  const [activeChat, setActiveChat] = useState<any | undefined>(undefined)
  const [loadingMessages, setLoadingMessages] = useState<boolean>(false)
  const [isConfirmLeaveModalOpen, setIsConfirmLeaveModalOpen] = useState<boolean>(false)
  const [loadingChatsHistory, setLoadingChatsHistory] = useState<boolean>(true)

  const { socket, onLogin } = useContext(SocketContext)

  const userId = useAppSelector((state: App.State) => state.auth.account.memberId)

  const scrollRef = useRef<HTMLInputElement>(null)

  const scrollToBottom = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'instant', inline: 'end' })
  }

  const createChat = useCallback((customerId: string | undefined) => {
    socket?.emit('chat:create', {
      customerId,
    })
  }, [socket])

  const onNewChat = useCallback(() => {
    if (!userId && activeChat) {
      setIsConfirmLeaveModalOpen(true)
      return
    }

    createChat(userId)
  }, [activeChat, createChat, userId])

  const onLoginToSaveClick = () => {
    setIsConfirmLeaveModalOpen(false)
    onLogin()
  }

  const onContinueWithoutSavingClick = () => {
    setIsConfirmLeaveModalOpen(false)

    setChatsHistory([])
    createChat(userId)
  }

  const onSelectChat = useCallback((chatId: string) => {
    setActiveChatId(chatId)
  }, [])

  const getAllChats = useCallback(() => {
    setLoadingChatsHistory(true)

    getSupportChats()
      .then((chats) => {
        setChatsHistory(chats)
        setActiveChat(chatsHistory[0])
        setActiveChatId(chatsHistory[0]?.id)
      })
      .finally(() => setTimeout(() => setLoadingChatsHistory(false), 250))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showModal = useContext(ModalContext)

  const openModal = useCallback(() => {
    showModal(<Modal title="" mode="menu" height="full" >
      <History loadingChatsHistory={loadingChatsHistory} chatsHistory={chatsHistory} activeChatId={activeChatId} onSelectChat={onSelectChat} onNewChat={onNewChat} />
    </Modal>)
  }, [activeChatId, chatsHistory, loadingChatsHistory, onNewChat, onSelectChat, showModal])

  useEffect(() => {
    scrollToBottom()
  })

  useEffect(() => {
    const sessionStorageChatId = sessionStorage.getItem('activeChatId')
    const sessionStorageChatSynced = sessionStorage.getItem('activeChatSynced') === 'true'

    // this is the case when non-logged-in user created chat and then logged in
    if (userId && sessionStorageChatId && !sessionStorageChatSynced) {
      updateChat({ id: sessionStorageChatId, customerId: userId }).then(() => {
        sessionStorage.setItem('activeChatSynced', 'true')

        getAllChats()
      })
      // this is normal flow when logged-in user comes to the page, we load all his chats
    } else if (userId) {
      getAllChats()
    } else {
      setTimeout(() => setLoadingChatsHistory(false), 250)
    }
  }, [getAllChats, userId])

  useEffect(() => {
    if (activeChatId && userId) {
      setLoadingMessages(true)

      getSupportChatById(activeChatId)
        .then((chat) => setActiveChat(chat))
        .finally(() => setTimeout(() => setLoadingMessages(false), 250))
    }
  }, [activeChatId, userId])

  useEffect(() => {
    if (socket) {
      socket.connect()
      socket.on('chat:created', (newChat) => {
        setChatsHistory((prevChatsHistory) => [newChat, ...prevChatsHistory])
        setActiveChat(newChat)
        setActiveChatId(newChat.id)
        sessionStorage.setItem('activeChatId', newChat.id)

        // this means that the chat was created by non-logged-in user via message send, not via new chat button
        if (!newChat.customerId) {
          sessionStorage.setItem('activeChatSynced', 'false')
        }
      })

      socket.on('chat:error', () => {
        showSnackbar(
          'Something went wrong, please try again later.',
          'critical',
          { heading: 'Error' },
        )
      })

      socket.on('chat:error:limit-reached', (message) => {
        showSnackbar(
          `Limit reached. ${message}`,
          'critical',
          { heading: 'Error' },
        )
      })

      return () => {
        socket.off('chat:created')
        socket.off('chat:error')
        socket.off('chat:error:limit-reached')
      }
    }
  }, [socket])

  useEffect(() => {
    if (socket) {
      socket.on('chat:title-set', (data) => {
        const chats = chatsHistory.map((chat) => {
          if (chat.id === data.chatId) {
            return {
              ...chat,
              title: data.title,
            }
          }
          return chat
        })
        setChatsHistory(chats)
      })

      return () => {
        socket.off('chat:title-set')
      }
    }
  }, [socket, chatsHistory])

  useEffect(() => {
    if (socket && chatsHistory[0]?.status === ChatStatuses.ACTIVE && !activeChat) {
      setActiveChat(chatsHistory[0])
      setActiveChatId(chatsHistory[0]?.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, chatsHistory])

  useEffect(() => {
    if (socket) {
      socket.on('chat:update', (updatedChat) => {
        if (updatedChat.id === activeChatId) {
          setActiveChat(updatedChat)
        }
        setChatsHistory(chatsHistory.map((chat) => {
          if (chat.id === updatedChat.id) {
            return updatedChat
          }

          return chat
        },
        ))
      })

      return () => {
        socket.off('chat:update')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, activeChat])

  return (
    <StyledLayoutContainer>
      <CSSBreakpoint max="mobile">
        <ScrollTo ref={scrollRef} />
      </CSSBreakpoint>
      <HistoryContainer min="tablet">
        <History loadingChatsHistory={loadingChatsHistory} chatsHistory={chatsHistory} activeChatId={activeChatId} onSelectChat={onSelectChat} onNewChat={onNewChat} />
      </HistoryContainer>
      <ChatContainer>
        <Chat loadingMessages={loadingMessages || loadingChatsHistory} socket={socket} activeChat={activeChat} onNewChat={onNewChat} onSelectChat={onSelectChat} toggleHistory={openModal} userId={userId} />
      </ChatContainer>
      <SupportContainer min="tablet">
        <Support />
      </SupportContainer>
      <Modal
        title="Would you like to save the current chat?"
        isOpen={isConfirmLeaveModalOpen}
        onClose={() => setIsConfirmLeaveModalOpen(false)}
        primaryActionText="Login to save chat"
        secondaryActionText="Continue without saving"
        onPrimaryActionClick={onLoginToSaveClick}
        onSecondaryActionClick={onContinueWithoutSavingClick}
      >
        <StyledBodyText variant="large">
          Login to your Luxury Escapes account to save your chats with LESA and access anytime to continue where you left off.
        </StyledBodyText>
      </Modal>
    </StyledLayoutContainer>
  )
}

export default memo(ChatPage)
